import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import ReactQuill from 'react-quill';
import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Table from '../../CommonComponents/Table';
import { useInfiniteScroll } from '../../../Hooks/useInfiniteScroll';
import useAxiosPrivate from '../../../Hooks/useAxiosPrivate';
import { useDebouncedValue } from '../../../Hooks/useDebounceValue';
import AlertContext from '../../../context/AlertContext';
import { RESULT_STATUS } from '../../../constants';
import { LibraryContext } from '../../../context/LibraryContext';
import Modal from '../../CommonComponents/Modal';
import InputField from '../../CommonComponents/InputField';
import Button from '../../Button';
import Toast from '../../CommonComponents/Toast';
import { useEffect } from 'react';
import { useLayoutEffect } from 'react';

const sectionOrder = {
  "Operators": 1,
  "Conditional Statements": 2,
  "Iterations": 3,
  "Patterns": 4,
  "Series": 5,
  "Arrays": 6,
  "Strings": 7,
  "Sorting": 10,
  "Time Complexity": 11,
  "Two Pointers": 13,
  "Sliding Window": 14,
  "Prefix Array": 15,
  "Recursion": 8,
  "Searching": 9,
  "Hashing": 12,
  "Linked List": 16,
  "Stack": 17,
  "Queue": 18,
  "Tree": 19,
  "Graph": 20,
  "Greedy Algorithm": 21,
  "Dynamic Programming": 23,
  "Bit Manipulation": 24,
  "Backtracking": 22,
}

const AdminCcSection = () => {

  const { type } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { setShowNotify } = useContext(AlertContext);
  const { searchText, addSectionModal, setAddSectionModal, setSearchText, sectionName, setSectionName } = useContext(LibraryContext);

  const axios = useAxiosPrivate();
  const debouncedSearchText = useDebouncedValue(searchText, 500);


  const [openMenuId, setOpenMenuId] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);


  const [toastType, setToastType] = useState("warning");
  const [openToast, setOpenToast] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [sort, setSort] = useState(null);
  const handleSortChange = (newSort) => {
    setSort(newSort);
  };

  useLayoutEffect(() => {
    setSearchText("");
  }, []);



  const fetchQuestions = useCallback(
    async ({ page, pageSize }) => {
      try {
        const url = `node/admin/cc/section?name=${debouncedSearchText}`;
        const { data } = await axios.get("node/admin/cc/section", {
          params: {
            name:debouncedSearchText,
          },
          headers: { "Content-type": "application/json" },
        });
        if (data.resultCode === RESULT_STATUS.SUCCESS) {
          const resdata =
            typeof data.data === "string" ? JSON.parse(data.data) : data.data;
          const res = typeof resdata[0].data === "string" ? JSON.parse(resdata[0].data) : resdata[0].data;
          const transFormedData = res
            .slice()
            .sort((a, b) => {
              const orderA = sectionOrder[a.name] ?? Infinity;
              const orderB = sectionOrder[b.name] ?? Infinity;
              const comparison = orderA - orderB;
              return sort?.direction === "desc" ? -comparison : comparison;
            })
            .map((item, index) => ({
              ...item,
              sno: (page - 1) * pageSize + index + 1,
            }));
          return { data: transFormedData, total: data.count };
        } else
          throw new Error(
            "Unable to fetch the question list. Please try again later.",
          );
      } catch (error) {
        let isUnAuth = error?.response?.status === 403;
        if (error?.name === "CanceledError" || error?.name === "AbortError")
          return;
        if (isUnAuth) {
          setShowNotify({
            show: true,
            msg: "You have been logged-out due to inactivity. Login again.",
            title: "Warning !",
            isUnAuth,
          });
        }
        setOpenToast(true);
        setErrMsg(error.message ?? "Something went wrong");
        setToastType("error");
      }
    },
    [axios, type, setShowNotify, debouncedSearchText, sort],
  );

  const {
    containerRef,
    sentinelRef,
    loading,
    hasMore,
    items: sections,
    setItems: setSections,
    refresh,
  } = useInfiniteScroll({
    fetchFn: fetchQuestions,
    pageSize: 10,
    resetKey: JSON.stringify({ debouncedSearchText, type, sort }),
  });


  const onRowClick = async (row) => {
    navigate(`/library/${type}/cc/section/${row.id}`);
    setSectionName(row.name ?? "");
  };

  const TABLE_COLUMNS = useMemo(
    () => [
      { key: "sno", title: "S.No" },
      {
        key: "name",
        title: "SECTIONS",
        sortable: true,
      },

      {
        key: "easy",
        title: "EASY"
      },
      {
        key: "medium",
        title: "MEDIUM",
      },
      {
        key: "hard",
        title: "HARD",
      },
      {
        key: "total",
        title: "TOTAL",
      },
    ],
    [type],
  );

  console.log("sections:", sections);

  return (
    <div>
      <Toast
        open={openToast}
        onClose={() => setOpenToast(false)}
        message={errMsg}
        type={toastType}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      // slots={{ transition: Slide }}
      />
      <Table
        ref={containerRef}
        className={`library-question-table`}
        columns={TABLE_COLUMNS}
        data={sections}
        onRowClick={onRowClick}
        bordered
        stripped={false}
        fixedHeader
        infiniteScroll
        sentinelRef={sentinelRef}
        loadingMore={loading}
        hasMore={hasMore}
        isMultiSelect={false}
        sort={sort}
        onSortChange={handleSortChange}
      />
      <Modal
        isOpen={addSectionModal}
        onClose={() => setAddSectionModal(false)}
        showCloseIcon={false}
        className="library-section-add-modal"
      >
        <AddNewSection setAddSectionModal={setAddSectionModal} refresh={refresh} />
      </Modal>
    </div>
  )
}

const AddNewSection = ({ setAddSectionModal, refresh }) => {

  const axios = useAxiosPrivate();
  const { setShowNotify } = useContext(AlertContext);

  const [name, setName] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [toastType, setToastType] = useState("warning");
  const [openToast, setOpenToast] = useState(false);
  const [errMsg, setErrMsg] = useState("");


  const handleOnChangeName = (value) => {
    setName(value);
    if (value.trim().length == 0) {
      setErrors((prev) => ({
        ...prev,
        name: "Name is required"
      }));
    }
    else {
      setErrors({});
    }
  };

  const addNewSectionFunction = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(`node/admin/cc/section`, {
        name
      });
      if (response.data.resultCode === RESULT_STATUS.SUCCESS) {
        console.log("response?.data:", response?.data);
        setOpenToast(true);
        setErrMsg(response?.data?.data ?? "Successfully added a section");
        setToastType("error");
        setAddSectionModal(false);
        refresh();
      }
      else {
        throw new Error("Something went wrong");
      }
    } catch (error) {
      let isUnAuth = error?.response?.status === 403;
      if (error?.name === "CanceledError" || error?.name === "AbortError")
        return;
      if (isUnAuth) {
        setShowNotify({
          show: true,
          msg: "You have been logged-out due to inactivity. Login again.",
          title: "Warning !",
          isUnAuth,
        });
      }
      setOpenToast(true);
      setErrMsg(error.message ?? "Something went wrong");
      setToastType("error");
    }
    finally {
      setIsLoading(false);
    }
  }

  return (
    <div className='section-add-pop-up-container'>
      <Toast
        open={openToast}
        onClose={() => setOpenToast(false)}
        message={errMsg}
        type={toastType}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      // slots={{ transition: Slide }}
      />
      <header className='section-add-pop-up-header-container'>
        <div className='section-add-pop-up-header-name-wrapper' onClick={() => { setAddSectionModal(false) }}>
          <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 11L1 6L6 1" stroke="#667085" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <div className='section-add-pop-up-header-title'>Create Section</div>
        </div>
      </header>
      <div className='section-add-pop-up-header-filter-wrapper'>
        <InputField
          label="Section"
          name="section"
          value={name ?? ""}
          placeholder="Enter the Section name here"
          onChange={(event) => handleOnChangeName(event.target.value)}
          hintText={errors?.name}
          destructive={!!errors?.name}
        />
      </div>
      <div class="section-add-question-list-btn-wrapper">
        <Button
          size="sm"
          hierarchy={{ type: "secondaryGrey", buttonText: "Cancel" }}
          onClick={() => {
            setAddSectionModal(false);
          }}
        />
        <Button
          size="sm"
          hierarchy={{
            type: "primary",
            buttonText: isLoading ? "Adding..." : "Add",
          }}
          disable={isLoading || errors.name || name.trim().length == 0}
          onClick={() => {
            addNewSectionFunction();
          }}
        />
      </div>
    </div>
  )
}


export default AdminCcSection;